import { Component } from './Component';
import { TimelineStore } from '../store/TimelineStore';
import { togglePlay, stepForward, stepBackward, setSpeed } from '../store/TimelineController';
import { createLogger } from '../utils/logger';

const log = createLogger('TimelineSlider');

export class TimelineSlider extends Component {
  private _keyHandler!: (e: KeyboardEvent) => void;

  render(): HTMLElement {
    const { currentDecade, minDecade, maxDecade, playing, speed } = TimelineStore.getState();

    const el = document.createElement('div');
    el.className = 'timeline-slider';
    el.setAttribute('data-testid', 'timeline-slider');

    el.innerHTML = `
      <div class="timeline-main-row">
        <button
          class="play-pause-btn"
          aria-label="${playing ? 'Pause' : 'Play'}"
          data-testid="play-pause-btn"
        >${playing ? '&#9646;&#9646;' : '&#9654;'}</button>
        <span class="decade-label" data-testid="decade-label">${currentDecade}</span>
        <input
          type="range"
          class="decade-range"
          data-testid="decade-range"
          min="${minDecade}"
          max="${maxDecade}"
          step="10"
          value="${currentDecade}"
          aria-label="Timeline decade"
        />
        <div class="speed-group" role="group" aria-label="Playback speed">
          <button class="speed-btn${speed === 1 ? ' active' : ''}" data-speed="1" aria-pressed="${speed === 1}" data-testid="speed-1x">1&times;</button>
          <button class="speed-btn${speed === 2 ? ' active' : ''}" data-speed="2" aria-pressed="${speed === 2}" data-testid="speed-2x">2&times;</button>
          <button class="speed-btn${speed === 5 ? ' active' : ''}" data-speed="5" aria-pressed="${speed === 5}" data-testid="speed-5x">5&times;</button>
        </div>
      </div>
    `;

    return el;
  }

  mount(parent: HTMLElement): void {
    super.mount(parent);
    this._wireEvents();
    this.track(TimelineStore.subscribe(() => this._sync()));
    this._keyHandler = this._onKeyDown.bind(this);
    document.addEventListener('keydown', this._keyHandler);
    log.info('TimelineSlider mounted');
  }

  unmount(): void {
    document.removeEventListener('keydown', this._keyHandler);
    super.unmount();
  }

  // ── Private ────────────────────────────────────────────────────────────────

  private _wireEvents(): void {
    const rangeEl = this.el.querySelector<HTMLInputElement>('.decade-range')!;
    rangeEl.addEventListener('input', () => {
      TimelineStore.setState({ currentDecade: Number(rangeEl.value) });
    });

    const playBtn = this.el.querySelector<HTMLButtonElement>('.play-pause-btn')!;
    playBtn.addEventListener('click', () => togglePlay());

    this.el.querySelectorAll<HTMLButtonElement>('.speed-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        setSpeed(Number(btn.dataset.speed) as 1 | 2 | 5);
      });
    });
  }

  private _sync(): void {
    const { currentDecade, minDecade, maxDecade, playing, speed } = TimelineStore.getState();

    const rangeEl = this.el.querySelector<HTMLInputElement>('.decade-range')!;
    rangeEl.min = String(minDecade);
    rangeEl.max = String(maxDecade);
    rangeEl.value = String(currentDecade);

    const labelEl = this.el.querySelector<HTMLSpanElement>('.decade-label')!;
    labelEl.textContent = String(currentDecade);

    const playBtn = this.el.querySelector<HTMLButtonElement>('.play-pause-btn')!;
    playBtn.setAttribute('aria-label', playing ? 'Pause' : 'Play');
    playBtn.innerHTML = playing ? '&#9646;&#9646;' : '&#9654;';

    this.el.querySelectorAll<HTMLButtonElement>('.speed-btn').forEach((btn) => {
      const isActive = Number(btn.dataset.speed) === speed;
      btn.classList.toggle('active', isActive);
      btn.setAttribute('aria-pressed', String(isActive));
    });
  }

  private _onKeyDown(e: KeyboardEvent): void {
    // Skip when a text input (not a range) has focus
    if (e.target instanceof HTMLInputElement && e.target.type !== 'range') {
      return;
    }
    if (e.target instanceof HTMLTextAreaElement) {
      return;
    }

    if (e.code === 'Space') {
      e.preventDefault();
      togglePlay();
    } else if (e.code === 'ArrowRight') {
      e.preventDefault();
      stepForward();
    } else if (e.code === 'ArrowLeft') {
      e.preventDefault();
      stepBackward();
    }
  }
}
